<template>
  <div class="current_keywords">
    <div v-if="combinedChangedKeywords.length > 0">
      <div class="table_wrapper">
        <table
          :class="{'reviews-enabled': reviewEnabled}"
          class="keywords_table head"
        >
          <thead>
            <tr>
              <td class="keyword">
                <div>
                  <span>Keyword</span>
                </div>
              </td>
              <td>
                <div
                  v-tooltip="'Search volume is a measure of the average number of searches that are performed through Google per month'"
                >
                  <span>Search Volume</span>
                </div>
              </td>
              <td>
                <div
                  v-tooltip="difficultyTooltip"
                >
                  <span>Difficulty</span>
                </div>
              </td>
              <td v-if="reviewEnabled">
                <div
                  v-tooltip="'Receive feedback on each keyword, determining if we think it\'s a highly relevant keyword for your selected industry. Use this to help guide you in making your keyword choices.'"
                >
                  <span>Review</span>
                </div>
              </td>
              <td>
                &nbsp;
              </td>
            </tr>
          </thead>
        </table>
        <div class="table_body_wrap">
          <table
            :class="{'reviews-enabled': reviewEnabled}"
            class="keywords_table"
          >
            <tbody>
              <tr
                v-for="(keyword, index) in combinedChangedKeywords"
                :key="index"
              >
                <td class="keyword p_small">
                  <span
                    v-if="keyword.new"
                    class="new"
                  >
                    New
                  </span>
                  <span
                    v-else-if="keyword.remove"
                    class="remove"
                  >
                    Removed
                  </span>
                  <span>
                    {{ keyword.keyword }}
                    <i
                      v-if="keyword?.assigned_page"
                      v-tooltip="{
                        content: `This keyword is assigned to the page - <strong>${ getURLPath(keyword.assigned_page) }</strong>`,
                        html: true,
                      }"
                      class="far fa-sitemap xs-ml-5"
                    />
                  </span>
                </td>
                <td class="volume">
                  {{ keyword.search_volume > 0 ? keyword.search_volume.toLocaleString() : '< 10' }}
                </td>
                <td class="difficulty">
                  <difficulty-tag
                    :difficulty="
                      keyword.difficulty > 0 && keyword.difficulty !== 999
                        ? keyword.difficulty
                        : keyword.calculated_difficulty
                    "
                  />
                </td>
                <td v-if="reviewEnabled">
                  <keyword-review-status
                    v-if="!keyword.remove"
                    :keyword-review="getKeywordReview(keyword)"
                  />
                </td>
                <td>
                  <button
                    v-if="keyword.remove"
                    class="add_button"
                    @click="ignoreRemoveChosenKeyword(index)"
                  />
                  <button
                    v-else
                    class="remove_btn red_btn"
                    @click="removeNewChosenKeyword(index)"
                  >
                    <svg
                      fill="none"
                      height="14"
                      viewBox="0 0 14 14"
                      width="14"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2.1875 7H11.8125"
                        stroke="white"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-else
      class="no_results"
    >
      No changes to your chosen keywords
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import DifficultyTag from "../../../../../../../js/components/global/assets/DifficultyTag.vue";
import KeywordReviewStatus from "../components/KeywordReviewStatus.vue";

export default {
  components: {
    KeywordReviewStatus,
    DifficultyTag,
  },
  data() {
    return {
      pollCount: 0,
      difficultyTooltip: 'Keyword difficulty is a metric indicating how difficult it is to rank in the '+
        'top 10 search results for a keyword. Try to pick keywords with a lower difficulty value where '+
        'possible, as it should be easier to rank in Google.'
    }
  },
  computed: {
    ...mapGetters('keywordIdeas', [
      'chosenKeywords',
      'existingReviews',
      'newChosenKeywords',
      'removeChosenKeywords',
      'reviewEnabled',
      'keywordReviews',
    ]),
    combinedChangedKeywords() {
      let newKeywords = [
        ...this.removeChosenKeywords.map(keyword => {
          keyword.remove = 1;
          return keyword;
        }),
        ...this.newChosenKeywords.map(keyword => {
          keyword.new = 1;
          return keyword;
        }),
      ];

      if (this.reviewEnabled) {
        // This should return new keywords and existing keywords that haven't been reviewed or the decision is null
        newKeywords = [
          ...newKeywords,
          ...this.chosenKeywords
            .filter(keyword =>
              !this.existingReviews
                .find(existing => existing.keyword_index_id === keyword.keyword_index_id)?.decision
            ),
        ]
      }

      return newKeywords;
    },
  },
  methods: {
    ...mapActions('keywordIdeas', [
      'removeNewChosenKeyword',
      'ignoreRemoveChosenKeyword',
      'fetchExistingReviews',
    ]),
    getKeywordReview(keyword) {
      // We mix keyword models so this can either be a keyword or a keywordIndex model.
      const keywordIndexId = keyword.keyword_index_id ? keyword.keyword_index_id : keyword.id;
      return this.keywordReviews?.find(({keyword_index_id}) => keyword_index_id === keywordIndexId);
    },
    getURLPath(url) {
      try {
        let urlObject = new URL(url);
        return urlObject.pathname;
      } catch (e) {
        return url;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.table_wrapper {
  margin-bottom: 10px;

  .table_body_wrap {
    overflow-y: scroll;

    @media screen and (max-height: 1000px) {
      height: 450px;
    }

    @media screen and (min-height: 1001px) {
      height: 500px;
    }
  }
}

.keywords_table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;

  &.reviews-enabled {
    tr td {
      &:nth-of-type(2) {
        width: 20%;
      }

      &:nth-of-type(3) {
        width: 20%;
      }

      &:nth-of-type(4) {
        width: 10%;
      }

      &:nth-of-type(5) {
        width: 10%;
      }
    }
  }

  thead td {
    span {
      font-weight: 500;
      font-size: 16px;
      line-height: 26px;
      letter-spacing: -0.25px;
      margin-right: 0.25rem;
    }
  }

  tbody tr td {
    .new,
    .remove {
      margin-right: 15px;
      padding: 0.25rem 0.5rem;
      font-size: 10px;
      line-height: 1.3;
      letter-spacing: -0.1px;
      border-radius: 6px;
    }

    .new {
      background-color: #DFF5EE;
      color: #37B2A8;
    }

    .remove {
      background-color: var(--difficulty-very-high-color);
      color: var(--difficulty-very-high-font-color);
    }
  }

  tr td {
    padding: 1rem;

    &.keyword {
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      align-items: center;
      width: 100%;
    }

    &:nth-of-type(2) {
      width: 25%;
      text-align: center;
    }

    &:nth-of-type(3) {
      width: 25%;
      text-align: center;
    }

    &:nth-of-type(4) {
      width: 10%;
      text-align: center;
    }
  }
}

.add_button {
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-radius: 6px;
  border: 2px solid #37B2A8;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.1875 7H11.8125' stroke='%2337B2A8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M7 2.1875V11.8125' stroke='%2337B2A8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat;
  background-position: center;
}

.remove_btn {
  border-radius: 8px;
  width: 32px;
  height: 32px;
  background-color: #F34F4F !important;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no_results {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-bottom: 10px;
  font-size: 1.75em;
  font-style: normal;
  height: 100px;
}
</style>
